import React, { useState } from "react";
import {
  Box,
  Grid,
  Container,
  Typography,
  Fade,
  Skeleton,
} from "@mui/material";
import { mediaUrl } from "../../../../utils/cfImage";

const SKELETON_COUNT = 8;

const imageFrameSx = {
  position: "relative",
  width: "100%",
  aspectRatio: "4 / 5",
  bgcolor: "#f3f3f1",
  overflow: "hidden",
};

function ImageSkeleton() {
  return (
    <Box sx={{ position: "absolute", inset: 0 }}>
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: "100%",
          height: "100%",
          minHeight: "100%",
          borderRadius: 0,
          transform: "none",
        }}
      />
    </Box>
  );
}

function GalleryCard({ imageUrl, alt, label }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "#fff",
        overflow: "hidden",
        transition: "box-shadow 0.35s ease",
        "&:hover": {
          boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
          "& img": {
            transform: "scale(1.04)",
          },
        },
      }}
    >
      <Box sx={imageFrameSx}>
        {!loaded && <ImageSkeleton />}
        <Fade in={loaded} timeout={500}>
          <Box
            component="img"
            src={imageUrl}
            alt={alt}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transition: "transform 0.6s ease",
            }}
          />
        </Fade>
      </Box>

      {label && (
        <Typography
          align="center"
          sx={{
            fontFamily: "Spartan-Bold",
            fontSize: { xs: 11, sm: 12 },
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#000",
            py: 2,
            px: 1.5,
            borderTop: "1px solid rgba(158,159,147,0.35)",
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

function GallerySkeleton() {
  return (
    <Box sx={{ height: "100%", bgcolor: "#fff", overflow: "hidden" }}>
      <Box sx={imageFrameSx}>
        <ImageSkeleton />
      </Box>
      <Skeleton
        variant="text"
        animation="wave"
        sx={{ mx: "auto", my: 2, width: "60%" }}
      />
    </Box>
  );
}

export default function ImagesGallery(props) {
  const { component } = props;

  const { fscope } = component.props;
  const devHelper = component.getHelpers("dev");
  const filesUrl = component.getHelpers("env").get("files_url");
  const sliders = component.getData("default.sliders", []);
  const slidersLoaded = component.getData("default.slidersLoaded", false);
  const imgVersion = component.getHelpers("value").getValue("images-version");

  const isLoading = !slidersLoaded;
  const imageAlt = component.trans(`${fscope} image`);

  return (
    <Box sx={{ bgcolor: "#fafaf8", py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        <Grid
          container
          justifyContent="center"
          rowSpacing={{ xs: 2.5, md: 3.5 }}
          columnSpacing={{ xs: 1.5, md: 2.5 }}
        >
          {isLoading &&
            Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <Grid item xs={6} sm={4} md={3} key={`gallery-skeleton-${index}`}>
                <GallerySkeleton />
              </Grid>
            ))}

          {!isLoading &&
            sliders.length === 0 && (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  sx={{
                    fontFamily: "Spartan-Medium",
                    color: "rgba(0,0,0,0.55)",
                    py: 6,
                  }}
                >
                  {component.trans("no-items-found")}
                </Typography>
              </Grid>
            )}

          {!isLoading &&
            sliders.map((slider, index) => {
              const { slug = null, button_text } = devHelper.getObjectValue(
                slider,
                "_values",
              );

              if (!slug) return null;

              return (
                <Grid item xs={6} sm={4} md={3} key={slug}>
                  <Fade in timeout={400 + index * 60}>
                    <Box>
                      <GalleryCard
                        imageUrl={mediaUrl(filesUrl, `${fscope}/${slug}.jpg`, {
                          width: 700,
                          height: 900,
                          version: imgVersion,
                        })}
                        alt={button_text || imageAlt}
                        label={button_text}
                      />
                    </Box>
                  </Fade>
                </Grid>
              );
            })}
        </Grid>
      </Container>
    </Box>
  );
}
